/**
 * In-person quote capture form — /quote/in-person. Shown under the instant
 * estimate; replaces the old "email my results" form (name + phone + email).
 *
 * The builder's inputs and the quote breakdown arrive as `context`, built by
 * inPersonQuoteContext below. The builder starts pre-filled, so a value the
 * visitor never changed is labelled "(default)" in the Note and is not written
 * to CRM fields — only figures the visitor actually entered are.
 */
import { CARD_BANDS } from "@/utils/pricingEngine";
import { company, email, fullName, phoneRequired } from "./common";

/** Legacy analytics name kept so quote_requested / email_capture reports continue. */

const K = {
  turnover: "Monthly card turnover (£)",
  atv: "Average transaction value (£)",
  terminals: "Card machines",
  epos: "EPOS required",
  monthly: "Estimated monthly cost",
};

const gbp = (n) =>
  `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => (typeof v === "number" && v > 0 ? v : null);

/**
 * `inputs` are the builder's raw strings, `initial` its starting values.
 * Unchanged pre-filled values become "<value> (default)" strings, which
 * contextToCrm ignores (it only takes numbers).
 */
export function inPersonQuoteContext(inputs, results, initial) {
  if (!results) return {};
  const value = (key, parse) => {
    const n = parse(inputs[key]);
    if (!(n > 0)) return null;
    return String(inputs[key]) === String(initial[key]) ? `${n} (default)` : n;
  };
  const custom = inputs.cardMixMode === "advanced";
  return {
    [K.turnover]: value("monthlyTurnover", parseFloat),
    [K.atv]: value("averageTransactionValue", parseFloat),
    [K.terminals]: value("terminalCount", (v) => parseInt(v, 10)),
    [K.epos]: inputs.eposRequired ? "Yes" : "No",
    "EPOS stations": inputs.eposRequired ? value("eposStationCount", (v) => parseInt(v, 10)) : null,
    "Card mix": custom
      ? `Custom: ${CARD_BANDS.map((b) => `${b.label} ${parseFloat(inputs[`mix_${b.key}`]) || 0}%`).join(", ")}`
      : "Standard",
    "Processing fees": `${gbp(results.processingCost)}/month`,
    "Terminal rental": `${gbp(results.terminalCost)}/month`,
    "EPOS software": inputs.eposRequired ? `${gbp(results.eposCost)}/month` : null,
    "PCI compliance": `${gbp(results.pciCost)}/month`,
    "Monthly transactions": Math.round(results.transactionCount),
    [K.monthly]: gbp(results.estimatedMonthlyCost),
    "Estimated annual cost": gbp(results.estimatedAnnualCost),
  };
}

const quoteInPerson = {
  id: "quote_in_person",
  label: "In-person Quote",
  crm: { layout: "Standard", leadSource: "Website Quote Builder" },
  fields: [
    [fullName, company],
    [email, { ...phoneRequired, help: "We'll only call to discuss your quote — never spam." }],
  ],
  submitLabel: "Request my tailored quote",
  consent: { variant: "standard" },
  success: {
    title: "Thank you",
    body: "We'll prepare a personalised quote based on these figures and be in touch shortly.",
  },
  contextToCrm: (ctx) => ({
    Monthly_Card_Turnovers: num(ctx[K.turnover]),
    ATV: num(ctx[K.atv]),
    Number_of_card_machines: num(ctx[K.terminals]),
    Needs: ctx[K.epos] === "Yes" ? ["Card machine", "EPOS"] : ["Card machine"],
    How_do_you_take_payments_now: ["Card machine"],
  }),
  summary: (values, ctx) =>
    ctx[K.monthly] ? `In-person quote: estimate ${ctx[K.monthly]} a month` : "In-person quote",
};

export default quoteInPerson;
